import type {
  ConsultaDeLinhas,
  DecisoesDaImportacao,
  ImportacaoDetalhada,
  ListaDeImportacoes,
  OpcoesDaAnalise,
  PaginaDeLinhas,
  RevisaoDaImportacao,
} from '@arena/shared/schemas';
import { api, enviarArquivo } from './cliente.js';

/**
 * As chamadas da importação de planilha — `#m-importar` e a tela de
 * conferência.
 *
 * Os tipos são os de `@arena/shared/schemas`, os mesmos que o backend usa
 * para validar e responder.
 *
 * Ver: Arena_backend/Docs/plano/09-importacao-csv.md
 */

/**
 * O que o servidor aceita como tipo de um CSV. O navegador declara o que o
 * sistema operacional acha: "application/vnd.ms-excel" no Windows com
 * Excel, vazio em muitos casos — e o .xlsx declara o tipo dele, que vai
 * como binário para o servidor reconhecer pela assinatura e responder com
 * a instrução de "Salvar como".
 */
const TIPOS_DE_CSV = new Set([
  'text/csv',
  'text/plain',
  'application/csv',
  'application/vnd.ms-excel',
]);

function tipoDoArquivo(arquivo: File): string {
  return TIPOS_DE_CSV.has(arquivo.type) ? arquivo.type : 'application/octet-stream';
}

export const importacaoApi = {
  /** "Analisar arquivo": o CSV vai cru no corpo, as escolhas na query. Nenhum aluno é gravado. */
  analisar(arquivo: File, opcoes: OpcoesDaAnalise): Promise<ImportacaoDetalhada> {
    const query = new URLSearchParams({
      nome: opcoes.nome,
      separador: opcoes.separador,
      codificacao: opcoes.codificacao,
      status: opcoes.status,
    });
    return enviarArquivo<ImportacaoDetalhada>(
      `/importacoes?${query.toString()}`,
      arquivo,
      tipoDoArquivo(arquivo),
    );
  },

  listar(): Promise<ListaDeImportacoes> {
    return api<ListaDeImportacoes>('/importacoes');
  },

  detalhe(id: string): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}`);
  },

  /** O relatório, filtrado por diagnóstico e paginado pelo número da linha. */
  linhas(id: string, consulta: Partial<ConsultaDeLinhas> = {}): Promise<PaginaDeLinhas> {
    const query = new URLSearchParams();
    if (consulta.diagnostico) query.set('diagnostico', consulta.diagnostico);
    if (consulta.apos) query.set('apos', String(consulta.apos));
    if (consulta.limite) query.set('limite', String(consulta.limite));
    const texto = query.toString();
    return api<PaginaDeLinhas>(`/importacoes/${id}/linhas${texto ? `?${texto}` : ''}`);
  },

  /** Corrigir o de-para ou a situação padrão — a planilha é reanalisada no servidor. */
  revisar(id: string, dados: RevisaoDaImportacao): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  decidir(id: string, dados: DecisoesDaImportacao): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}/decisoes`, { metodo: 'PUT', corpo: dados });
  },

  /** O servidor confere a base de novo antes de gravar — pode responder 409 com linhas mudadas. */
  confirmar(id: string): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}/confirmar`, { metodo: 'POST' });
  },

  retomar(id: string): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}/retomar`, { metodo: 'POST' });
  },

  descartar(id: string): Promise<ImportacaoDetalhada> {
    return api<ImportacaoDetalhada>(`/importacoes/${id}/descartar`, { metodo: 'POST' });
  },
};
